import React from 'react';
import {connect, ConnectedProps} from 'react-redux';
import {Link} from 'react-router-dom';
import {DropdownItem} from 'reactstrap';
import {IReduxStoreState} from '@src/@interfaces';
import ControlledPopupable from '@src/components/Popupable/ControlledPopupable';
import {ControlledPopupableProps} from '@src/@types/components/ControlledPopupable';
import avatarPlaceholder from '@client/assets/images/avatar-placeholder.gif';
import app from '@src/helpers/app';
import {BootstrapColors} from '@src/@types/Bootstrap';
import Translate from '@src/components/Translate/Translate';
import RenderIf from '@src/components/RenderIf/RenderIf';
import classnames from '@src/helpers/classnames';
import {UserContext} from '@src/contexts/UserContext';
import {appLoadInitValues} from '@src/_redux/app/actions';
import config from '@src/helpers/config';

type Props = ConnectedProps<typeof connector> & ControlledPopupableProps & {
    openSettingsPopup?: () => void,
};
type ItemProps = {
    label: string,
    to?: string
    route?: string,
    icon?: string,
    badge?: {
        color?: BootstrapColors,
        amount: number
    },
};

export class MiniProfile extends ControlledPopupable<Props> {
    protected getDropdownProps() {
        return {className: ''};
    }

    protected getDropdownItemProps(item: ItemProps) {
        return {
            tag: Link,
            to: item.to ?? app('url').route(item.route ?? '') ?? '#',
        };
    }

    protected getDropdownItems(): ItemProps[] {
        return [{
            label: 'MiniProfile.profile',
            route: 'profile@profile',
            icon: 'fas fa-user',
            badge: undefined,
        }];
    }

    protected getDropdownToggleProps() {
        return {
            tag: 'button',
            type: 'button',
            className: 'btn header__menu-item'
        };
    }

    protected renderDropdownToggleContent() {
        return <>
            <img src={this.props.user?.avatar_public_path ?? avatarPlaceholder}
                 className="rounded-circle header__user-avatar"
                 alt={this.props.user?.name}
            />
            <span className="d-none d-xl-inline-block mx-1">{this.props.user?.name}</span>
            <i className="fas fa-chevron-down font-size-10 d-none d-xl-inline-block"/>
        </>;
    }

    protected renderDropdownItemContent(item: ItemProps) {
        return <>
            <RenderIf
                    expression={item.badge}
                    render={() => {
                        const className = classnames({
                            'badge float-end': true,
                            [`bg-${item.badge?.color}`]: Boolean(item.badge?.color),
                        });

                        return <span className={className}>{item.badge?.amount}</span>;
                    }}
            />
            <RenderIf
                    expression={item.icon}
                    render={() => <i className={`${item.icon} font-size-13 align-middle me-1`}/>}
            />
            <Translate message={item.label}/>
        </>;
    }

    protected renderDropdownMenuItems() {
        return <>
            <UserContext.Consumer>{context => {
                if (
                        config('client.app-env', 'local') === 'production' &&
                        context?.model?.is_super_user !== true
                ) {
                    return null;
                }

                const item = {
                    label: 'MiniProfile.clear-cache',
                    route: '',
                    icon: 'fas fa-broom',
                };

                return <DropdownItem
                        key={this.resolveDropdownItemKey(item, -1)}
                        onClick={() => {
                            app('http').route('app.clear-cache').then(() => {
                                this.props.appLoadInitValues();
                            });
                        }}
                >
                    {this.renderDropdownItemContent(item)}
                </DropdownItem>;
            }}</UserContext.Consumer>

            {super.renderDropdownMenuItems()}

            <RenderIf
                    expression={this.props.openSettingsPopup}
                    render={openPopup => {
                        return <DropdownItem onClick={openPopup}>
                            {this.renderDropdownItemContent({
                                label: 'MiniProfile.settings',
                                route: '',
                                icon: 'fas fa-cogs',
                            })}
                        </DropdownItem>;
                    }}
            />

            <DropdownItem divider={true}/>

            {this.renderDropdownItem({
                label: 'MiniProfile.logout',
                route: 'auth@logout',
                icon: 'fas fa-power-off text-danger',
            } as ItemProps, 999)}
        </>;
    }
}

const mapState = (state: IReduxStoreState) => ({
    user: state.user.model,
    locale: state.locale.locale,
});

export const connector = connect(mapState, {appLoadInitValues});

export default connector(MiniProfile);
